@if (isReadOnly()) {
  <div class="w-full h-full">
    <div class="self-stretch justify-start text-text-color text-base font-medium font-['Inter'] leading-6 pb-4">Uploaded Documents</div>
    <div class=" max-h-[400px] overflow-auto">
      @if (uploadedDocuments().length > 0) {
        <p-table [value]="uploadedDocuments()"
                 size="small"
                 [tableStyle]="{ width: 'auto'}"
                 showGridlines>
          <ng-template #header>
            <tr>
              <th></th>
              <th>Filename</th>
            </tr>
          </ng-template>
          <ng-template #body let-document>
            <tr>
              <td>
                <div class="px-4">
                  <file-type-icons [type]="document.contentType" textColour="text-primary"></file-type-icons>
                </div>
              </td>
              <td>
                <div class="text-primary text-sm font-semibold font-['Inter'] cursor-pointer pr-4 max-w-[400px] whitespace-nowrap overflow-hidden overflow-ellipsis" (click)="downloadFile(document.fileId)">{{document.fileName}}</div>
              </td>
            </tr>
          </ng-template>
        </p-table>
      } @else {
        <div class="text-xs font-normal font-['Inter'] leading-[9.75px] px-4 py-2">
          <i>No uploaded documents.</i>
        </div>
      }
    </div>
  </div>
} @else {
  <div class="w-full h-full flex flex-col gap-6">
    <div class="max-w-1/2">
      <ta-toast title="CTM Booking Documentation"
                [body]="isFlightOnly()
                  ? 'You must upload the relevant CTM documents showing the costs of all flights.'
                  : 'If you are travelling by air or hire car, or you are booking accommodation, you must upload the relevant CTM documents showing the cost, or estimated cost, of the bookings.'"
                severity="info"
                [visible]="true"
                [closable]="false"
                size="max"
      ></ta-toast>
    </div>
    <p-card>
      <div class="flex gap-2">
        <div class="w-64 self-stretch p-6 bg-surface-0 rounded-tl-xl rounded-bl-xl shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)] inline-flex flex-col justify-start items-start gap-6 overflow-hidden">
          @if (isFlightOnly()) {
            <!-- Advisory only: upload is not enforced for flight-only TAs. -->
            <div class="self-stretch justify-start text-surface-800 text-base font-medium font-['Inter'] leading-6">Upload Flight Booking Documents</div>
            <div class="self-stretch justify-start text-surface-600 text-sm font-light font-['Inter']">Browse and select (or drag and drop) all documents required to support your TA.</div>
          } @else {
            <div class="self-stretch justify-start text-surface-800 text-base font-medium font-['Inter'] leading-6">Upload Supporting Documentation</div>
            <div class="self-stretch justify-start text-surface-600 text-sm font-light font-['Inter']">Browse and select (or drag and drop) all documents required to support your TA.</div>
            <div class="self-stretch flex flex-col justify-start items-start gap-3">
              <div class="self-stretch justify-start text-surface-800 text-sm font-medium font-['Inter'] leading-6">Required Documents</div>
              @if (requiredDocumentTypes().length > 0) {
                <div class="self-stretch justify-start text-surface-600 text-sm font-light font-['Inter']">Based on the information you’ve provided, as a minimum you should upload the following:</div>
                <ul class="list-disc list-inside self-stretch text-surface-600 text-sm font-light font-['Inter']">
                  @for (documentType of requiredDocumentTypes(); track documentType;) {
                    <li>{{ documentType }}</li>
                  }
                </ul>
              } @else {
                <div class="self-stretch justify-start text-surface-600 text-sm font-light font-['Inter'] italic">No required documentation.</div>
              }
            </div>
          }
        </div>

        <div class="w-full flex self-stretch gap-6 p-6 bg-surface-50 rounded-tr-xl rounded-br-xl shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)]">
          <upload-document [disabled]="uploadDisabled()" (filesSelected)="uploadFile({files: $event})"></upload-document>
          <div class="flex-1 max-h-[400px] overflow-auto flex flex-wrap gap-4 content-start">
            @for (uploading of uploadingDocuments(); track uploading.id) {
              <div class="w-96 h-fit bg-surface-0 rounded-xl outline outline-1 outline-offset-[-1px] outline-surface-300 flex flex-col px-4.5 py-3 gap-2">
                <div class="flex items-center gap-4">
                  <div class="">
                    <file-type-icons></file-type-icons>
                  </div>
                  <div class="flex flex-col">
                    <div class="text-surface-900 text-sm font-medium font-['Inter'] w-56 whitespace-nowrap overflow-hidden overflow-ellipsis">{{ uploading.name }}</div>
                    <div class="text-surface-400 text-xs font-normal font-['Inter']">{{ formatBytes(uploading.size) }} • <i class="fa-solid fa-spinner fa-spin"></i> Uploading {{ uploading.progress }}%</div>
                  </div>
                </div>
                <p-progressbar [value]="uploading.progress" [showValue]="false" [style]="{'height': '5px'}"></p-progressbar>
              </div>
            }
            @for (uploadedDoc of uploadedDocuments(); track uploadedDoc) {
              <div class="w-96 h-fit py-4 bg-surface-0 rounded-xl outline outline-1 outline-offset-[-1px] outline-surface-300 flex flex-col px-4.5 py-3 gap-2">
                <div class="flex items-center gap-4">
                  <div class="">
                    <file-type-icons [type]="uploadedDoc.contentType"></file-type-icons>
                  </div>
                  <div class="flex flex-col">
                    <div class="text-surface-900 text-sm font-medium font-['Inter'] w-56 whitespace-nowrap overflow-hidden overflow-ellipsis cursor-pointer hover:text-primary" (click)="downloadFile(uploadedDoc.fileId)">{{ uploadedDoc.fileName ?? '-' }}</div>
                    <div class="text-surface-400 text-xs font-normal font-['Inter']">{{ formatBytes(uploadedDoc.fileSizeBytes ?? 0)  }} • <i class="fa-solid fa-circle-check text-primary"></i> Complete</div>
                  </div>
                  <div class="flex-1"></div>
                  <div class="flex flex-col items-start">
                    <icon-btn variant="minus-red"
                            ariaLabel="Remove file"
                            (click)="removeFile(uploadedDoc)" />
                  </div>
                </div>
                <p-progressbar [value]="100" [showValue]="false" [style]="{'height': '5px'}"></p-progressbar>
              </div>
            }
          </div>
        </div>
      </div>
    </p-card>
  </div>
}
